<template>
  <div class="counselors container">
    
    <h1 class="sr-only">Counselor Search</h1>

    <div v-if="!counselors" class="row">
      <Loader style="width: 100%" />
    </div>

    <div v-else class="row">
      <div class="col-12 d-lg-none">
        <button
          class="modal-filters btn-secondary large-filters-btn"
          type="button"
          @click="showFilters()"
        >
          <font-awesome-icon :icon="['fas', 'sliders-v']" class="icon alt" />
          <span>Counselor Filters</span>
        </button>
      </div>
      <div class="col-12 col-lg-8">
        <div class="dashboard-card light">
          <p class="mb-0">
            <span v-if="counselors && counselors.length === 0">
              No counselors were found.
              <div class="d-lg-none">
                <button
                  class="btn modal-filters btn-primary mt-4"
                  type="button"
                  @click="showFilters()"
                >
                  Change Filters
                </button>
              </div>
            </span>
            <span v-else>
              These providers are pre-filtered and ranked based on the
              results of your mental health survey. You can use the filters to
              adjust and narrow down results. You can adjust your provider
              preferences in your account page.
            </span>
          </p>
        </div>

        <div v-if="counselors && counselors.length" class="counselor-list">
          <div
            v-for="counselor in counselors"
            :key="counselor.id"
            class="dashboard-card counselor"
          >
            <div class="row">
              <div class="col-12 col-lg-8">
                <div class="avatar-name">
                  <div class="left">
                    <div
                      v-if="counselor.imageUrl"
                      class="user-avatar"
                      :style="{
                        backgroundImage: `url(${counselor.imageUrl})`
                      }"
                    />
                    <div v-else class="user-avatar" />
                  </div>
                  <div class="right">
                    <h2 class="name">
                      {{ counselor.firstName }} {{ counselor.lastName }}
                    </h2>
                    <span class="title">
                      BetterMynd Verified Counselor
                    </span>
                    <span v-if="counselor.priceInCents" class="price d-lg-none">
                      <font-awesome-icon
                        v-if="loadingCreditsRemaining"
                        :spin="true"
                        :icon="['fas', 'spinner']"
                        class="icon alt"
                      />
                      <span v-else-if="userHasCredits">1 Credit</span>
                      <span v-else>
                        {{ (counselor.priceInCents / 100) | currency }}
                      </span>
                      <span class="price_sub-label">Per Session</span>
                    </span>
                  </div>
                </div>
              </div>
              <div class="d-none d-lg-block col-lg-4 text-right">
                <span v-if="counselor.priceInCents" class="price">
                  <span
                    class="
                      d-flex
                      flex-column
                      justify-content-center
                      align-items-center
                    "
                  >
                    <font-awesome-icon
                      v-if="loadingCreditsRemaining"
                      :spin="true"
                      :icon="['fas', 'spinner']"
                      class="icon alt"
                    />
                    <span v-else-if="userHasCredits">1 Credit</span>
                    <span v-else>{{ (counselor.priceInCents / 100) | currency }}</span>
                    <span class="price_sub-label">Per Session</span>
                  </span>
                </span>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <div class="lower">
                  <span v-if="hasAnyMatchingSpecialties(counselor)">
                    Specialties:
                  </span>
                  <ul class="pills">
                    <template v-for="s in counselor.specialties">
                      <li :key="s.id">
                        <font-awesome-icon
                          v-if="userHasStruggle(s)" 
                          :icon="['fas', 'check-circle']"
                          class="icon alt"
                          style="color:green"
                        />
                        {{ s.name }}
                      </li>
                    </template>
                  </ul>
                  
                  <span v-if="hasAnyMatchingPreferences(counselor)">
                    Matching Preferences:
                  </span>
                  <ul class="pills">
                    <template v-for="l in counselor.languages">
                      <li v-if="userPrefersLanguage(l)" :key="l.id">
                        Speaks {{ l.name }}
                      </li>
                    </template>
                    <li v-if="userPrefersEthnicity(counselor)">
                      Ethnicity
                    </li>
                    <li v-if="userPrefersGender(counselor)">
                      Gender
                    </li>
                    <li v-if="userPrefersReligion(counselor)">
                      Religion
                    </li>
                    <li v-if="userPrefersLgbtq(counselor)">
                      Lgbtq
                    </li>
                  </ul>
                  <div class="action-row mt-4">
                    <router-link
                      :to="`/student/counselor/profile/${counselor.id}`"
                      tag="button"
                      class="btn btn-ghost"
                    >
                      View Profile
                    </router-link>
                    <button
                      type="button"
                      class="btn btn-primary btn-wide"
                      @click="scheduleWithCounselor(counselor)"
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="dashboard-card">
            <button
              v-if="pageToken"
              :disabled="isLoading"
              type="button"
              class="btn btn-primary btn-wide"
              style="width:100%"
              @click="loadMore()"
            >
              <font-awesome-icon
                v-if="isLoading"
                :spin="true"
                :icon="['fas', 'spinner']"
                class="icon alt"
              />
              <span v-else>
                Load More
              </span>
            </button>
            <div v-else style="text-align:center;">
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                class="icon alt"
                style="color:green"
              />
              All Results Loaded
            </div>
          </div>
        </div>
      </div>

      <div class="d-none d-lg-block col-lg-4">
        <div class="dashboard-card filter-sidebar">
          <h2 style="font-size: 20px; margin-top: 7px">
            <span class="d-none d-xl-inline">Counselor </span>Filters:
          </h2>
          <button class="btn back-btn" type="button" @click="resetFilters()">
            <span>Reset</span>
            <font-awesome-icon :icon="['fas', 'undo']" class="icon alt" />
          </button>
          <filters v-model="filters" />
        </div>
      </div>
    </div>

    <modal-filters ref="modalFilters" v-model="filters" />
    <send-message-modal ref="sendMessageModal" />
    <vue-accessible-modal>
      <template v-slot:backdrop />
    </vue-accessible-modal>
  </div>
</template>

<script>
import SessionRequestModal from '../session-request/SessionRequestModal.vue'
import SendMessageModal from '@/components/modals/SendMessageModal.vue';
import Filters from './components/filters/Filters.vue';
import FiltersModel from './components/filters/FiltersModel.js';
import ModalFilters from './components/ModalFilters.vue';
import Loader from '@/components/Loader.vue';
import StudentClient from '@/api/StudentClient';
import UserClient from '@/api/UserClient';

export default {
  name: 'Counselors',
  components: {
    Loader,
    Filters,
    ModalFilters,
    SendMessageModal,
  },
  data() {
    return {
      studentClient: new StudentClient(this.$http),
      userClient: new UserClient(this.$http),

      loadingCreditsRemaining: true,
      userHasCredits: false,
      counselors: null,

      isLoading: false,
      pageToken: null,

      limit: 15,

      filters: new FiltersModel()
    };
  },

  watch: {
    filters() {
      this.loadCounselors();
    }
  },

  async mounted() {
    this.loadCounselors();
    try {
      const remainingCredits = await this.studentClient.getRemainingCredits();
      this.userHasCredits = remainingCredits.individual_session_credits_remaining > 0;
    } catch (ex) {
      console.error(ex);
    } finally {
      this.loadingCreditsRemaining = false
    }
  },

  methods: {
    showFilters() {
      this.$refs.modalFilters.show();
    },
    async loadMore() {
      try {
        this.isLoading = true;
        const payloadFilters = this.filters.toPayload();
        const user = this.$store.getters.currentUser;

        const response = await this.studentClient
          .getCounselors(user.id, this.pageToken, payloadFilters, this.limit);

        this.counselors.push(...response.counselors);
        this.pageToken = response.nextPageToken;
      } catch (ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
          msg: 'Could not retrieve more providers. Please try again.',
          error: true
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadCounselors() {
      try {
        this.isLoading = true;
        const payloadFilters = this.filters.toPayload();
        const user = this.$store.getters.currentUser;

        const response = await this.studentClient
          .getCounselors(user.id, null, payloadFilters, this.limit);

        this.counselors = response.counselors || [];
        this.pageToken = response.nextPageToken;
      } catch (ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
          msg: 'Could not retrieve providers. Please refresh the page to try again.',
          error: true
        });
      } finally {
        this.isLoading = false;
      }
    },

    scheduleWithCounselor(counselor) {
      if (this.$store.getters.currentUser.num_pending_payments > 0) {
        return this.$store.commit('setGlobalAlert', {
          msg: 'To schedule a session you must first complete your pending payments.',
          error: true
        });
      }
      this.$modal.show(
        SessionRequestModal, {
          props: {
            counselor: {
              ...counselor,
              name: `${counselor.firstName} ${counselor.lastName}`,
            }
          },
          listeners: {
            onerror: (err) => {
              console.error(err);
            },
            onsendamessage: (counselor) => {
              this.$refs.sendMessageModal.show(counselor.userId);
            },
            onrequest: (requestResult) => {
              this.$gtag.event('appointment_request', {
                student_id: requestResult.user.id,
                counselor_id: requestResult.counselor_id,
                start_time: requestResult.start_time,
              });
              this.$router.push({ name: 'dashboard', params: { from: 'event-scheduled' } });
            }
          }
        });
    },

    resetFilters() {
      this.filters = new FiltersModel();
    },

    userPrefersLanguage(language) {
      const user = this.$store.getters.currentUser;
      const language_preference = user.counseling_preferences.language_preference;

      return language.id === language_preference;
    },

    userPrefersEthnicity(counselor) {
      const user = this.$store.getters.currentUser;
      return user.counseling_preferences.race_preference
          && user.counseling_preferences.race_preference === counselor.ethnicity;
    },

    userPrefersGender(counselor) {
      const user = this.$store.getters.currentUser;
      return user.counseling_preferences.gender_preference
          && user.counseling_preferences.gender_preference === counselor.gender;
    },

    userPrefersReligion(counselor) {
      const user = this.$store.getters.currentUser;
      return user.counseling_preferences.religion_preference
          && user.counseling_preferences.religion_preference === counselor.religion;
    },

    userPrefersLgbtq(counselor) {
      const user = this.$store.getters.currentUser;
      return user.counseling_preferences.lgbtq_preference
          && user.counseling_preferences.lgbtq_preference === true
          && counselor.isLgbtq === true;
    },

    userHasStruggle(specialty) {
      const user = this.$store.getters.currentUser;
      const struggles = user.counseling_preferences.struggle_areas;
      
      for(const struggle of struggles)
        if(struggle.id === specialty.id)
          return true;

      return false;
    },

    hasAnyMatchingPreferences(counselor) {
      for(const language of counselor.languages)
        if(this.userPrefersLanguage(language))
          return true;

      return this.userPrefersEthnicity(counselor)
          || this.userPrefersGender(counselor)
          || this.userPrefersReligion(counselor)
          || this.userPrefersLgbtq(counselor);
    },

    hasAnyMatchingSpecialties(counselor) {
      for(const specialty of counselor.specialties)
        if(this.userHasStruggle(specialty))
          return true;

      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
.counselors {
  .upper-row {
    position: relative;
    margin-bottom: 30px;

    p {
      padding-top: 15px;
      margin-bottom: 0;
    }
  }

  .nav-buttons {
    position: absolute;
    right: -10px;
    top: 0;

    @media (max-width: $xs-max) {
      position: static;
      margin-top: 20px;
    }

    button {
      background: none;
      border: none;
      color: white;
      font-size: 30px;
      width: 35px;

      @media (max-width: $md-max) {
        width: 50px;
      }

      &:hover {
        color: $green;
      }

      &:disabled {
        opacity: 0.4;

        &:hover {
          color: inherit;
        }
      }
    }
  }

  .counselor-list {
    .counselor {
      @include border-radius($border-radius);

      padding: 30px;
      margin-bottom: 10px;

      p {
        color: rgba(white, 0.75);
        font-size: 18px;
        margin-bottom: 20px;
      }

      .spot-number {
        color: $green;
        margin-bottom: 30px;
        display: block;
        font-weight: bold;
      }
    }
  }

  .avatar-name {
    @media (max-width: $md-max) {
      margin-bottom: 5px;
    }

    .left {
      display: inline-block;
      vertical-align: top;

      .user-avatar {
        @include background-cover;
        @include border-radius(100px);

        background-image: url('~@/assets/img/default-user.jpg');
        height: 80px;
        width: 80px;
        margin-right: 20px;

        @media (max-width: $md-max) {
          margin-top: 3px;
        }
      }
    }

    .right {
      display: inline-block;
      vertical-align: top;
      width: calc(100% - 100px);

      .name {
        font-size: 28px;
        margin-top: 8px;

        @media (max-width: $md-max) {
          margin-top: 0;
          margin-bottom: 5px;
        }
      }

      .title {
        opacity: 0.75;
      }

      .price {
        @media (max-width: $md-max) {
          display: block;
          font-size: 1.25rem;
          text-transform: uppercase;
          font-weight: bold;
          letter-spacing: 0.01rem;
        }
      }
    }
  }

  .price {
    color: $green;
    font-weight: bold;

    @media (min-width: $lg-min) {
      display: flex;
      justify-content: flex-end;
      font-size: 22px;
      line-height: 1.33rem;
      font-family: $sans;
      margin-top: 8px;

      &_sub-label {
        font-size: 0.9rem;
        text-transform: uppercase;
        font-weight: normal;
        letter-spacing: 0.01rem;
      }

      .smaller {
        font-size: 18px !important;
      }
    }
  }

  .pills {
    li {
      text-transform: capitalize;
      @media (max-width: $sm-max) {
        width: auto;
      }
    }
  }

  .lower {
    margin-top: 20px;
    padding-left: 100px;

    @media (max-width: $sm-max) {
      padding-left: 0;
    }

    label {
      @include label;

      margin-top: 10px;
    }

    p {
      margin-top: 5px;
    }

    .action-row {
      .btn {
        min-width: 150px;

        @media (max-width: $sm-max) {
          width: 100%;
        }

        &:focus {
          outline-offset: 4px;
        }
      }

      & .btn:first-child {
        margin-right: 10px;

        @media (max-width: $sm-max) {
          margin-right: 0;
          margin-bottom: 10px;
        }
      }
    }
  }

  .replacement-label {
    text-transform: uppercase;
    font-size: 10px;
    opacity: 0.75;
    letter-spacing: 0.1rem;
    margin-bottom: 5px;
    color: white;
  }
}
</style>
